import { signal, computed, effect, untracked } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import type { QueryObserverOptions } from '@tanstack/react-query';
import { createStructuredSelector } from 'reselect';
import { injectable, inject } from 'src/features/ioc';
import { queryObserverSignal } from 'src/lib/signals/queryObserverSignal';
import type { ActiveUserModel } from 'src/models/ActiveUserModel';
import { activeUserModelReducer } from 'src/models/ActiveUserModel/reducer';
import type { ActiveUserModelState, ActiveUserModelAction } from 'src/models/ActiveUserModel/reducer';
import { getUserRolesQuery } from 'src/queries/user';
import type { RootState } from 'src/store';
import { clearMasq } from 'src/store/actions/auth';
import type { ReactBindings } from 'src/types/bindings';

const selectUserWithMasq = createStructuredSelector({
    user: (state: RootState) => state.auth.user,
    masqUser: (state: RootState) => state.auth.masqUser,
    isSuperUserModeEnabled: (state: RootState) => Boolean(state.ui.superuserAccessEnabledUntil),
    masqExpires: (state: RootState) => state.auth.masqAccessToken?.expires,
});

type UserData = ReturnType<typeof selectUserWithMasq>;

@injectable()
export class ActiveUserModelImpl implements ActiveUserModel {
    #env: string;
    #state = signal<ActiveUserModelState>({ type: 'initializing' });
    #store: ReactBindings['Store'];
    #queryObserverSignal: Signal<QueryObserverOptions<unknown, unknown, string[]>>;
    #rolesSignal: ReadonlySignal<string[]>;
    #currentUserId: Signal<string | undefined>;
    #dispatch = (action: ActiveUserModelAction) => {
        this.#state.value = activeUserModelReducer(this.#state.value, action);
    };

    // Cached computed signals
    #userName: ReadonlySignal<string>;
    #userEmail: ReadonlySignal<string | undefined>;
    #isMasquerading: ReadonlySignal<boolean>;
    #isSuperuser: ReadonlySignal<boolean>;
    #isSuperUserModeEnabled: ReadonlySignal<boolean>;
    #masqExpires: ReadonlySignal<UserData['masqExpires']>;
    #isDeveloper: ReadonlySignal<boolean>;
    #isSuperUserAlertingEnabled: ReadonlySignal<boolean>;
    #canToggleSuperUserAlerting: ReadonlySignal<boolean>;
    #userRoles: ReadonlySignal<string[]>;
    #disposableStack = new DisposableStack();

    constructor(
        @inject('Store') store: ReactBindings['Store'],
        @inject('QueryObserverModelFactory') queryObserverModelFactory: ReactBindings['QueryObserverModelFactory'],
        @inject('ConfigService') configService: ReactBindings['ConfigService'],
    ) {
        this.#store = store;
        this.#env = configService.environmentName;

        const userData = selectUserWithMasq(this.#store.getState());
        this.#currentUserId = signal(this.#getCurrentUserId(userData));

        // Determine initial superUserAlerting state
        const currentIsDeveloper =
            userData.masqUser !== undefined ? userData.masqUser.isDeveloper : (userData.user?.isDeveloper ?? false);

        const superUserAlerting = signal(this.#env === 'prod' ? true : !currentIsDeveloper);

        // Initialize cached computed signals
        this.#userName = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return '';
            }

            const { userData } = state;
            const activeUser = userData.masqUser ?? userData.user;
            return activeUser ? `${activeUser.firstName} ${activeUser.lastName}` : '';
        });

        this.#userEmail = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return undefined;
            }

            const { userData } = state;
            return userData.masqUser?.email ?? userData.user?.email;
        });

        this.#isMasquerading = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return false;
            }

            return state.userData.masqUser !== undefined;
        });

        this.#isSuperuser = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return false;
            }

            return state.userData.user?.isSuperuser ?? false;
        });

        this.#isSuperUserModeEnabled = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return false;
            }

            return state.userData.isSuperUserModeEnabled;
        });

        this.#masqExpires = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return undefined;
            }

            return state.userData.masqExpires;
        });

        this.#isDeveloper = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return false;
            }

            const { userData } = state;
            return userData.masqUser !== undefined
                ? userData.masqUser.isDeveloper
                : (userData.user?.isDeveloper ?? false);
        });

        this.#isSuperUserAlertingEnabled = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return false;
            }

            return state.userData.isSuperUserModeEnabled && state.superUserAlerting.value;
        });

        this.#canToggleSuperUserAlerting = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return false;
            }

            const isDeveloper =
                state.userData.masqUser !== undefined
                    ? state.userData.masqUser.isDeveloper
                    : (state.userData.user?.isDeveloper ?? false);

            return isDeveloper && this.#env !== 'prod';
        });

        this.#userRoles = computed(() => {
            const state = this.#state.value;
            if (state.type === 'initializing') {
                return [];
            }

            return state.roles;
        });

        this.#queryObserverSignal = computed(() => {
            return {
                ...getUserRolesQuery(this.#currentUserId.value),
                enabled: Boolean(this.#currentUserId),
            };
        });

        // Make the query observer
        const queryObserverModel = queryObserverModelFactory<unknown, unknown, string[]>(
            this.#queryObserverSignal.value,
        );
        this.#disposableStack.defer(
            effect(() => {
                queryObserverModel.setQueryOptions(this.#queryObserverSignal.value);
            }),
        );

        // Use the query observer signal to derive roles
        const queryObserverSignalDisposable = this.#disposableStack.use(
            queryObserverSignal(queryObserverModel.queryObserver),
        );

        this.#rolesSignal = computed(() => queryObserverSignalDisposable.querySignal.value.data ?? []);
        this.#disposableStack.defer(
            effect(() => {
                const roles = this.#rolesSignal.value;
                untracked(() => {
                    this.#dispatch({ type: 'UPDATE_ROLES', roles });
                });
            }),
        );

        // Initialize state as initialized
        this.#dispatch({
            type: 'INITIALIZE_SUCCESS',
            userData,
            roles: queryObserverSignalDisposable.querySignal.value.data ?? [],
            env: this.#env,
            superUserAlerting,
        });

        // Subscribe to store changes for user data
        this.#store.subscribeSelector(selectUserWithMasq).handle((newUserData) => {
            this.#dispatch({
                type: 'UPDATE_USER_DATA',
                userData: newUserData,
            });

            // Update roles observer if user ID changed
            const newUserId = this.#getCurrentUserId(newUserData);
            this.#currentUserId.value = newUserId;
        });
    }

    #getCurrentUserId(userData: UserData): string | undefined {
        // When masquerading, use masqUser's ID, otherwise use regular user's ID
        return userData.masqUser?.id ?? userData.user?.id;
    }

    // Computed signals derived from state
    get userId(): ReadonlySignal<string | undefined> {
        return this.#currentUserId;
    }

    get userName(): ReadonlySignal<string> {
        return this.#userName;
    }

    get userEmail(): ReadonlySignal<string | undefined> {
        return this.#userEmail;
    }

    get isMasquerading(): ReadonlySignal<boolean> {
        return this.#isMasquerading;
    }

    get isSuperuser(): ReadonlySignal<boolean> {
        return this.#isSuperuser;
    }

    get isSuperUserModeEnabled(): ReadonlySignal<boolean> {
        return this.#isSuperUserModeEnabled;
    }

    get masqExpires(): ReadonlySignal<UserData['masqExpires']> {
        return this.#masqExpires;
    }

    get isDeveloper(): ReadonlySignal<boolean> {
        return this.#isDeveloper;
    }

    get isSuperUserAlertingEnabled(): ReadonlySignal<boolean> {
        return this.#isSuperUserAlertingEnabled;
    }

    get canToggleSuperUserAlerting(): ReadonlySignal<boolean> {
        return this.#canToggleSuperUserAlerting;
    }

    // New user roles functionality
    get userRoles(): ReadonlySignal<string[]> {
        return this.#userRoles;
    }

    // Methods
    toggleSuperUserMode(enabled: boolean) {
        this.#store.dispatch({
            type: 'superuser::toggle',
            adminModeEnabled: enabled,
        });
    }

    /**
     * This can only be toggled by the user if they are a developer in non-production environments
     * this is not intended to be used by non-developers
     *
     * In production, developers cannot toggle alerting (it's always enabled)
     * In non-production environments, this is intended for developers to turn the superuser alerting on for
     * debugging purposes should such a need arise,
     * but normally developers won't see or hear the alerting
     */
    toggleSuperUserAlerting(enabled: boolean) {
        if (!this.canToggleSuperUserAlerting.value) {
            return;
        }

        this.#dispatch({
            type: 'UPDATE_SUPER_USER_ALERTING',
            enabled,
        });
    }

    stopMasq() {
        this.#store.dispatch(clearMasq());
    }

    [Symbol.dispose]() {
        this.#disposableStack.dispose();
    }
}
